const DAY_NAMES = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];
/** `YYYY-MM-DD` in the viewer's own timezone. */
export function dateKey(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
/**
 * `YYYY-MM-DD` → a local midnight Date. Deliberately not `new Date(str)`,
 * which parses a bare date as UTC and lands on the previous day for anyone
 * west of Greenwich.
 */
export function parseDateKey(key) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(key);
    if (!match)
        return null;
    const [, y, m, d] = match;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(date.getTime()) ? null : date;
}
/** Shift a date key by whole days, staying in local time. */
export function shiftDateKey(key, days) {
    const date = parseDateKey(key);
    if (!date)
        return key;
    date.setDate(date.getDate() + days);
    return dateKey(date);
}
/**
 * Monday of the week containing this date — the app's one Monday-first week.
 * Null for a key that isn't a date. Cheaper than `weekKeys(d)[0]` when only
 * the Monday is wanted (the fitness streak asks once per workout).
 */
export function mondayKey(anchor) {
    const date = parseDateKey(anchor);
    if (!date)
        return null;
    const dow = date.getDay();
    return shiftDateKey(anchor, dow === 0 ? -6 : 1 - dow);
}
/** Monday-first week containing this date, as seven keys. */
export function weekKeys(anchor) {
    const monday = mondayKey(anchor);
    if (!monday)
        return [];
    return Array.from({ length: 7 }, (_, i) => shiftDateKey(monday, i));
}
/** A datetime or date string reduced to its local date key. */
function toKey(value) {
    if (!value)
        return null;
    // Already a plain date — take it as written rather than round-tripping it
    // through Date, which would reinterpret it as UTC.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value))
        return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : dateKey(parsed);
}
/** Day of the month, clamped into a month that may be shorter. */
function clampDayOfMonth(day, year, monthIndex) {
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    return Math.min(Math.max(day, 1), lastDay);
}
/**
 * The day of the month a monthly habit falls on.
 *
 * `Recurrence.day_of_month` when the user set one. Otherwise the day the habit
 * was first scheduled for, or failing that the day it was created — which is
 * what someone means by "monthly" when they never said which day.
 */
export function monthlyAnchorDay(habit) {
    const explicit = habit.recurrence?.day_of_month;
    if (typeof explicit === 'number' && explicit >= 1 && explicit <= 31)
        return explicit;
    const source = toKey(habit.scheduled_for) ?? toKey(habit.created_at);
    const parsed = source ? parseDateKey(source) : null;
    return parsed ? parsed.getDate() : null;
}
/** Is the habit paused on this date? Mirrors `Scheduler.isHabitPaused`. */
export function habitPausedOn(habit, date) {
    if (!habit.is_paused)
        return false;
    const until = toKey(habit.paused_until);
    // Paused with no resume date is paused indefinitely.
    if (!until)
        return true;
    return date <= until;
}
/**
 * The first date this habit can be due — it cannot have been missed before it
 * existed. `scheduled_for` wins when set, because that is the user saying
 * "start this on the 1st"; otherwise the creation date.
 */
export function habitStartKey(habit) {
    return toKey(habit.scheduled_for) ?? toKey(habit.created_at);
}
/**
 * Is this habit due on this date?
 *
 * The single answer. `Scheduler`, the sidebar widget and the streak all go
 * through here, so they can no longer drift apart.
 */
export function habitScheduledOn(habit, date) {
    return habitScheduleOf(habit)(date);
}
/**
 * `habitScheduledOn` with the habit's half worked out once — the start, end,
 * pause, weekdays and monthly anchor — for a caller asking about many days.
 * The same answer, not a second rule: `habitScheduledOn` is this, called once.
 *
 * Pass the day's local-midnight `Date` too when you have it (a walk does), and
 * the key isn't parsed again.
 */
export function habitScheduleOf(habit) {
    const never = () => false;
    if (habit.is_archived)
        return never;
    const pausedUntil = habit.is_paused ? toKey(habit.paused_until) : null;
    // Paused with no resume date is paused indefinitely.
    if (habit.is_paused && !pausedUntil)
        return never;
    const recurrence = habit.recurrence;
    if (!recurrence?.frequency)
        return never;
    const frequency = recurrence.frequency;
    const start = habitStartKey(habit);
    const end = toKey(recurrence.end_date);
    let onDay;
    if (frequency === 'daily') {
        onDay = () => true;
    }
    else if (frequency === 'weekly') {
        const days = (recurrence.days_of_week ?? []).map((d) => d.toLowerCase());
        if (days.length === 0) {
            // No days chosen is a half-finished habit, not a habit that never
            // happens: fall back to the weekday it started on, which is what the
            // user was looking at when they said "weekly".
            const startDate = start ? parseDateKey(start) : null;
            const startWeekday = startDate ? startDate.getDay() : null;
            onDay = (day) => startWeekday !== null && startWeekday === day.getDay();
        }
        else {
            const due = DAY_NAMES.map((name) => days.includes(name));
            onDay = (day) => due[day.getDay()];
        }
    }
    else if (frequency === 'monthly') {
        const anchor = monthlyAnchorDay(habit);
        if (anchor === null)
            return never;
        // The 31st still comes due in February — on the 28th (or 29th), the same
        // way every calendar app resolves it. Without the clamp a habit set on the
        // 31st would skip half the year.
        onDay = (day) => day.getDate() === clampDayOfMonth(anchor, day.getFullYear(), day.getMonth());
    }
    else {
        return never;
    }
    return (date, day) => {
        if (pausedUntil && date <= pausedUntil)
            return false;
        if (start && date < start)
            return false;
        if (end && date > end)
            return false;
        const parsed = day ?? parseDateKey(date);
        if (!parsed)
            return false;
        return onDay(parsed);
    };
}
/** The dates in `[from, to]` on which this habit is due, oldest first. */
export function habitDueDates(habit, from, to) {
    const out = [];
    if (from > to)
        return out;
    let cursor = from;
    // Bounded so a malformed range can't spin: a year and a half of days is far
    // more than any view asks for.
    for (let guard = 0; guard < 550 && cursor <= to; guard += 1) {
        if (habitScheduledOn(habit, cursor))
            out.push(cursor);
        cursor = shiftDateKey(cursor, 1);
    }
    return out;
}
/**
 * Consecutive *due* days completed, counting back from today.
 *
 * Only days the habit was actually due can break it — a Mon/Wed/Fri habit
 * untouched on Tuesday is not a lapse, and the old code counted it as one for
 * every habit that wasn't daily. Today is allowed to be outstanding: a streak
 * shouldn't read as broken at 9am because you haven't got to it yet.
 */
export function habitStreak(habit, instances, today, habitId, { maxDays = 400 } = {}) {
    const id = habitId ?? habit.id;
    if (!id)
        return 0;
    const done = new Set(instances.filter((i) => i.habit_id === id).map((i) => i.date));
    const start = habitStartKey(habit);
    let streak = 0;
    let cursor = today;
    for (let guard = 0; guard < maxDays; guard += 1) {
        if (start && cursor < start)
            break;
        if (habitScheduledOn(habit, cursor)) {
            if (done.has(cursor)) {
                streak += 1;
            }
            else if (cursor === today) {
                // Still open. Neither credited nor counted against.
            }
            else {
                break;
            }
        }
        cursor = shiftDateKey(cursor, -1);
    }
    return streak;
}
/**
 * Due, done, streaks and weekday split for one habit, in one forward pass.
 *
 * The habit's page and Insights → Analytics each had their own loop for this,
 * built on `new Date(dateString)` — which is UTC midnight, so in the Americas
 * every weekday was read as the day before and a Mon/Wed/Fri habit was
 * "due" on Tue/Thu/Sat — the day after each real one. They also counted every stored completion against
 * the due days (so a rate could pass 100%), broke the current streak at 9am
 * because today wasn't done yet, and dropped monthly habits. This walks the
 * same predicate the planner uses.
 *
 * An archived habit is read as it was while active, up to the day it was
 * archived: its history is still its history, and `habitScheduledOn` (rightly,
 * for planning) says an archived habit is never due.
 */
/**
 * A habit as it was while it was live: an archived habit is read as active up
 * to the day it was archived (`habitScheduledOn` — rightly, for planning —
 * says an archived habit is never due). `end` is the last day it can count.
 */
export function historicalSubject(habit) {
    if (!habit.is_archived)
        return { subject: habit, end: null };
    return { subject: { ...habit, is_archived: false }, end: toKey(habit.archived_at) };
}
export function habitRecord(habit, instances, from, to, today, habitId) {
    const id = habitId ?? habit.id;
    const weekdays = DAY_NAMES.map((_, weekday) => ({ weekday, due: 0, completed: 0 }));
    const record = {
        due: 0, completed: 0, percentage: null, completions: 0,
        currentStreak: 0, longestStreak: 0, weekdays,
    };
    if (!id)
        return record;
    const done = new Set();
    for (const instance of instances) {
        if (instance.habit_id !== id)
            continue;
        done.add(instance.date);
        if (instance.date >= from && instance.date <= to)
            record.completions += 1;
    }
    const historical = historicalSubject(habit);
    const subject = historical.subject;
    let end = to < today ? to : today;
    if (historical.end && historical.end < end)
        end = historical.end;
    const start = habitStartKey(subject);
    let cursor = start && start < from ? start : from;
    const day = parseDateKey(cursor);
    if (!day)
        return record;
    const scheduled = habitScheduleOf(subject);
    let run = 0;
    // Twenty years of days: far past any real habit, short of a runaway loop.
    for (let guard = 0; guard < 7300 && cursor <= end; guard += 1) {
        const weekday = day.getDay();
        if (scheduled(cursor, day)) {
            const hit = done.has(cursor);
            const open = !hit && cursor === today;
            if (hit) {
                run += 1;
                if (run > record.longestStreak)
                    record.longestStreak = run;
            }
            else if (!open) {
                run = 0;
            }
            if (cursor >= from && !open) {
                record.due += 1;
                weekdays[weekday].due += 1;
                if (hit) {
                    record.completed += 1;
                    weekdays[weekday].completed += 1;
                }
            }
        }
        // One local day on, as shiftDateKey does — without re-parsing the key.
        day.setDate(day.getDate() + 1);
        cursor = dateKey(day);
    }
    record.currentStreak = run;
    record.percentage = record.due > 0 ? (record.completed / record.due) * 100 : null;
    return record;
}
/**
 * Per-day adherence across a set of habits.
 *
 * `completed` is intersected with what was actually due, which is the fix for
 * the bar that could exceed 100%: an instance belonging to a habit since
 * paused, archived, deleted, or simply not due that day no longer inflates the
 * numerator it was never in the denominator of.
 */
export function habitAdherence(habits, instances, dates) {
    return dates.map((date) => {
        const dueIds = habits.filter((h) => habitScheduledOn(h, date)).map((h) => h.id);
        const dueSet = new Set(dueIds);
        const completed = new Set(instances
            .filter((i) => i.date === date && dueSet.has(i.habit_id))
            .map((i) => i.habit_id)).size;
        const due = dueIds.length;
        return {
            date,
            due,
            completed,
            percentage: due > 0 ? (completed / due) * 100 : 0,
            restDay: due === 0,
        };
    });
}
/**
 * One row per habit for the given days, plus its streak.
 *
 * Habits with nothing due in the window come back with `active: false` rather
 * than being dropped, so a caller can choose between hiding them and showing
 * them greyed. The widget hides them: a row of seven blank squares for a
 * monthly habit in a week it isn't due told the user nothing.
 */
export function habitWeekSummaries(habits, instances, dates, today) {
    return habits.map((habit) => {
        const done = new Set(instances.filter((i) => i.habit_id === habit.id).map((i) => i.date));
        const days = dates.map((date) => ({
            date,
            due: habitScheduledOn(habit, date),
            completed: done.has(date),
        }));
        return {
            id: habit.id,
            title: habit.title,
            streak: habitStreak(habit, instances, today, habit.id),
            days,
            active: days.some((d) => d.due),
        };
    });
}
/** Habits due today, as a count — what the widget's header should say. */
export function habitsDueOn(habits, date) {
    return habits.filter((h) => habitScheduledOn(h, date)).map((h) => h.id);
}
/** Narrow a full Task to the fields scheduling cares about. */
export function scheduleSubject(task) {
    return {
        recurrence: task.recurrence,
        is_paused: task.is_paused,
        paused_until: task.paused_until,
        is_archived: task.is_archived,
        created_at: task.created_at,
        scheduled_for: task.scheduled_for,
    };
}
/**
 * Roll per-day adherence up into one figure.
 *
 * Lived inline in HabitsWidget, which meant the only other caller that wanted
 * "how are my habits going over a period" — the assistant's read_stats — had
 * to sum it again, and a second summation is a second answer.
 *
 * `percentage` is null rather than 0 when nothing was due. A week you had no
 * habits scheduled is not a week you failed, and reporting 0% for it is the
 * kind of wrong number that gets read aloud and believed.
 */
export function adherenceTotals(days) {
    let due = 0;
    let completed = 0;
    for (const day of days) {
        due += day.due;
        completed += day.completed;
    }
    return { due, completed, percentage: due > 0 ? (completed / due) * 100 : null };
}
//# sourceMappingURL=habit-schedule.js.map